// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — block category descriptors
// ═══════════════════════════════════════════════════════════════════════════
// Authored block / block-edit / insert command descriptors:
//   BLOCK          — `geometry/create-block-definition` (+ `create-block-insert`
//                    when postAction='convert')
//   INSERT         — `geometry/create-block-insert`
//   BEDIT          — `space/enter-block-edit` (NOT in COVERAGE_ALLOWLIST →
//                    descriptor must claim it)
//   BCLOSE         — `space/exit-block-edit`  (action save | discard)
//   BSAVE          — `space/save-block-edit`
//   EXPLODEINSERT  — `geometry/explode-insert` (block-specific INSERT explode;
//                    the general-purpose EXPLODE — handling PLINE / HATCH /
//                    DIM etc. — stays with commands/modify.ts; aliases here
//                    are deliberately disjoint to avoid CLI collision).
// Reducer locations were located via
// `grep -n 'case "<literal>"' apps/web/src/app/state/{geometryReducer,blockEditReducer}.ts`.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const GEOMETRY_REDUCER = "apps/web/src/app/state/geometryReducer.ts";
const BLOCK_EDIT_REDUCER = "apps/web/src/app/state/blockEditReducer.ts";

export const BLOCK_DESCRIPTORS: readonly CommandDescriptor[] = [
  {
    id: "BLOCK",
    category: "block",
    status: "implemented",
    aiCallable: true,
    // D5 — App.tsx's shellKeyboardCommandAliases table collapses bare "I"
    // onto the BLOCK draft flow (mirroring the full-word "INSERT"→BLOCK
    // collapse in draftingHelpers.ts's SPECIAL_CASE_OVERRIDES), but real
    // AutoCAD's "I" alias belongs to INSERT and the registry already
    // claims it there (below). Adding "I" here too would collide
    // (C.ALIAS). The app-specific I→BLOCK shell-keyboard shortcut is
    // preserved as a LOCAL override in useCommandLine.ts's derivation.
    aliases: { en: ["BLOCK", "B"], ru: ["БЛОК", "Б"] },
    commandTypeLiterals: [
      "geometry/create-block-definition",
      "geometry/create-block-insert",
    ],
    draftCommandId: "BLOCK",
    draftSteps: [
      {
        stepId: "block-select-objects",
        promptKeyRu: "prompt.block.select_objects",
        promptKeyEn: "prompt.block.select_objects",
        expects: "selection",
      },
      {
        stepId: "block-base-point",
        promptKeyRu: "prompt.block.base_point",
        promptKeyEn: "prompt.block.base_point",
        expects: "point",
      },
      {
        stepId: "block-dialog",
        promptKeyRu: "prompt.block.dialog",
        promptKeyEn: "prompt.block.dialog",
        expects: "dialog",
      },
    ],
    parameters: [
      {
        name: "blockDef.name",
        type: "string",
        required: true,
        description: {
          ru: "Имя нового определения блока (BLOCK NAME).",
          en: "Name of the new block definition (BLOCK NAME).",
        },
      },
      {
        name: "blockDef.basePoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Базовая точка определения (точка вставки экземпляра).",
          en: "Base point of the block definition (insertion origin).",
        },
      },
      {
        name: "blockDef.entities",
        type: "entityIdList",
        required: true,
        description: {
          ru: "Сущности, попадающие в определение блока.",
          en: "Entities included in the block definition.",
        },
      },
      {
        name: "blockDef.layer",
        type: "string",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Слой определения блока (по умолчанию 0).",
          en: "Layer the block definition is authored on (default 0).",
        },
      },
      {
        name: "blockDef.description",
        type: "string",
        required: false,
        description: {
          ru: "Текстовое описание блока (поле Description в диалоге).",
          en: "Textual description of the block (Description field in the dialog).",
        },
      },
      {
        name: "blockRecord.insertionUnits",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Единицы вставки (DXF group 70 — INSUNITS, 0..21).",
          en: "Insertion units (DXF group 70 — INSUNITS, 0..21).",
        },
      },
      {
        name: "blockRecord.explodable",
        type: "boolean",
        required: false,
        defaultValue: "true",
        description: {
          ru: "Разрешить разбивку (EXPLODE) экземпляров блока.",
          en: "Allow EXPLODE of the block's instances.",
        },
      },
      {
        name: "blockRecord.scalable",
        type: "enum",
        enumValues: ["uniform", "non-uniform"],
        required: false,
        defaultValue: "non-uniform",
        description: {
          ru: "Разрешённая модель масштабирования экземпляра.",
          en: "Allowed scaling mode for instances.",
        },
      },
      {
        name: "blockRecord.isAnnotative",
        type: "boolean",
        required: false,
        defaultValue: "false",
        description: {
          ru: "Аннотативный блок (поддержка annotation scale).",
          en: "Annotative block (annotation scale support).",
        },
      },
      {
        name: "postAction",
        type: "enum",
        enumValues: ["retain", "convert", "delete"],
        required: true,
        description: {
          ru: "retain — оставить исходники, convert — заменить INSERTом, delete — удалить.",
          en: "retain — keep source entities, convert — replace with INSERT, delete — remove.",
        },
      },
      {
        name: "convertSpaceId",
        type: "string",
        required: false,
        description: {
          ru: "spaceId для INSERT при postAction='convert'.",
          en: "spaceId for the INSERT created when postAction='convert'.",
        },
      },
      {
        name: "convertInsertPoint",
        type: "point2d",
        required: false,
        description: {
          ru: "Точка вставки конвертированного INSERT (обычно basePoint).",
          en: "Insertion point for the converted INSERT (usually basePoint).",
        },
      },
    ],
    help: {
      ru: {
        short: "Создать определение блока из выбранных сущностей.",
        long: "Команда BLOCK собирает выбранные сущности в новое определение блока: пользователь выбирает объекты, указывает базовую точку, открывается диалог Block Definition (имя, единицы, поведение, аннотативность, описание). По OK в чертёж записывается определение блока (geometry/create-block-definition) и, если выбрано Convert, дополнительно создаётся INSERT на месте исходных сущностей.",
        examples: ["BLOCK", "Б"],
        seeAlso: ["INSERT", "BEDIT", "BSAVE", "BCLOSE"],
      },
      en: {
        short: "Create a block definition from the selected entities.",
        long: "The BLOCK command bundles the selected entities into a new block definition: the user selects objects, specifies a base point, then the Block Definition dialog opens (name, units, behavior flags, annotativity, description). On OK the definition is written (geometry/create-block-definition) and, if Convert was chosen, an INSERT is created in place of the source entities.",
        examples: ["BLOCK", "B"],
        seeAlso: ["INSERT", "BEDIT", "BSAVE", "BCLOSE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.block.label",
      labelKeyEn: "command.block.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/create-block-definition",
        file: GEOMETRY_REDUCER,
      },
      {
        commandTypeLiteral: "geometry/create-block-insert",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  {
    id: "INSERT",
    category: "block",
    status: "implemented",
    aiCallable: true,
    aliases: { en: ["INSERT", "I"], ru: ["ВСТАВИТЬ", "В"] },
    commandTypeLiterals: ["geometry/create-block-insert"],
    draftCommandId: "INSERT",
    draftSteps: [
      {
        stepId: "insert-dialog",
        promptKeyRu: "prompt.insert.dialog",
        promptKeyEn: "prompt.insert.dialog",
        expects: "dialog",
      },
      {
        stepId: "insert-specify-point",
        promptKeyRu: "prompt.insert.specify_point",
        promptKeyEn: "prompt.insert.specify_point",
        expects: "point",
      },
      {
        stepId: "insert-specify-scale",
        promptKeyRu: "prompt.insert.specify_scale",
        promptKeyEn: "prompt.insert.specify_scale",
        expects: "distance",
      },
      {
        stepId: "insert-specify-rotation",
        promptKeyRu: "prompt.insert.specify_rotation",
        promptKeyEn: "prompt.insert.specify_rotation",
        expects: "angle",
      },
      {
        stepId: "insert-collect-attribs",
        promptKeyRu: "prompt.insert.attribute_value",
        promptKeyEn: "prompt.insert.attribute_value",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "blockName",
        type: "string",
        required: true,
        description: {
          ru: "Имя определения блока, который нужно вставить.",
          en: "Name of the block definition to insert.",
        },
      },
      {
        name: "insertionPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Точка вставки экземпляра блока.",
          en: "Insertion point of the block instance.",
        },
      },
      {
        name: "scaleX",
        type: "number",
        required: false,
        defaultValue: "1",
        description: {
          ru: "Масштаб по оси X (отрицательный — зеркально).",
          en: "X-axis scale factor (negative mirrors).",
        },
      },
      {
        name: "scaleY",
        type: "number",
        required: false,
        defaultValue: "1",
        description: {
          ru: "Масштаб по оси Y. Если != scaleX — non-uniform scale.",
          en: "Y-axis scale factor. If != scaleX it is non-uniform scale.",
        },
      },
      {
        name: "rotation",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Угол поворота экземпляра в радианах.",
          en: "Rotation angle of the instance, in radians.",
        },
      },
      {
        name: "spaceId",
        type: "string",
        required: true,
        description: {
          ru: "Пространство (model / layout / block-edit), куда помещается INSERT.",
          en: "Space (model / layout / block-edit) the INSERT is placed in.",
        },
      },
    ],
    help: {
      ru: {
        short: "Вставить существующее определение блока в чертёж.",
        long: "Команда INSERT открывает диалог Insert: пользователь выбирает имя блока (или вводит вручную), задаёт точку вставки, масштаб и поворот (или указывает их на экране), при необходимости заполняет значения атрибутов, после чего на чертёже создаётся новый INSERT, ссылающийся на выбранное определение блока.",
        examples: ["INSERT", "В", "I"],
        seeAlso: ["BLOCK", "BEDIT", "ATTDEF", "DDATTE"],
      },
      en: {
        short: "Insert an existing block definition into the drawing.",
        long: "The INSERT command opens the Insert dialog: the user picks a block name (or types one), specifies the insertion point, scale and rotation (optionally on-screen), fills in attribute values when needed, and a new INSERT entity referencing the chosen block definition is added to the drawing.",
        examples: ["INSERT", "I"],
        seeAlso: ["BLOCK", "BEDIT", "ATTDEF", "DDATTE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.insert.label",
      labelKeyEn: "command.insert.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/create-block-insert",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  {
    id: "BEDIT",
    category: "block",
    status: "implemented",
    // ТЗ §10.1: BEDIT AI-callable = no. The block editor is an interactive
    // authoring session; the AI never enters it directly (it authors block
    // definitions via the CREATE_BLOCK / CREATE_DYNAMIC_BLOCK safe-contour
    // actions, which target BLOCK, not BEDIT).
    aiCallable: false,
    aliases: { en: ["BEDIT", "BE"], ru: ["РЕДБЛОК"] },
    commandTypeLiterals: ["space/enter-block-edit"],
    draftCommandId: "BEDIT",
    draftSteps: [
      {
        stepId: "bedit-enter-name",
        promptKeyRu: "prompt.bedit.enter_name",
        promptKeyEn: "prompt.bedit.enter_name",
        expects: "text",
      },
      {
        stepId: "bedit-confirm-create",
        promptKeyRu: "prompt.bedit.confirm_create",
        promptKeyEn: "prompt.bedit.confirm_create",
        expects: "keyword",
        keywords: ["Y", "N"],
      },
    ],
    parameters: [
      {
        name: "blockName",
        type: "string",
        required: true,
        description: {
          ru: "Имя блока, открываемого в Block Editor.",
          en: "Name of the block to open in the Block Editor.",
        },
      },
      {
        name: "createIfMissing",
        type: "boolean",
        required: false,
        defaultValue: "false",
        description: {
          ru: "Создать новое определение, если блок с таким именем не найден.",
          en: "Create a new block definition when the named block does not exist.",
        },
      },
    ],
    help: {
      ru: {
        short: "Открыть редактор блоков для указанного определения.",
        long: "Команда BEDIT (РЕДБЛОК) запускает специальное пространство Block Editor для авторинга определения блока: добавление дочерних сущностей, ATTDEF, dynamic-параметров и actions. Если указано имя нового блока, при подтверждении создаётся пустое определение и сразу открывается на редактирование. Завершается через BCLOSE (save/discard) или BSAVE (быстрое сохранение).",
        examples: ["BEDIT", "РЕДБЛОК", "BE МойБлок"],
        seeAlso: ["BCLOSE", "BSAVE", "BLOCK", "ATTDEF"],
      },
      en: {
        short: "Open the Block Editor for the named block definition.",
        long: "The BEDIT command opens the dedicated Block Editor space where the user authors a block definition: adds child entities, ATTDEFs, dynamic parameters and actions. If a new name is supplied and the user confirms, an empty definition is created and immediately opened. The session is closed via BCLOSE (save/discard) or BSAVE (quick save).",
        examples: ["BEDIT", "BE MyBlock"],
        seeAlso: ["BCLOSE", "BSAVE", "BLOCK", "ATTDEF"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.bedit.label",
      labelKeyEn: "command.bedit.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "space/enter-block-edit",
        file: BLOCK_EDIT_REDUCER,
      },
    ],
  },

  {
    id: "BCLOSE",
    category: "block",
    status: "implemented",
    // Block-editor session lifecycle (save/discard + leave editor) — not an
    // AI-callable command (cf. ТЗ §10.1 ARRAYCLOSE = no, BEDIT = no).
    aiCallable: false,
    // D5 — "BC" is App.tsx's in-app short form (see BCLOSE descriptor
    // above) present in shellKeyboardCommandAliases but missing here.
    aliases: { en: ["BCLOSE", "BC"], ru: ["БЗАКРЫТЬ"] },
    commandTypeLiterals: ["space/exit-block-edit"],
    draftCommandId: "BCLOSE",
    parameters: [
      {
        name: "action",
        type: "enum",
        enumValues: ["save", "discard"],
        required: true,
        description: {
          ru: "save — сохранить изменения, discard — откатить к снимку, сделанному при входе.",
          en: "save — keep changes, discard — revert to the snapshot taken on enter.",
        },
      },
    ],
    help: {
      ru: {
        short: "Завершить сессию редактора блоков.",
        long: "Команда BCLOSE закрывает текущую сессию Block Editor. Пользователь выбирает action='save' (изменения остаются в определении блока) или action='discard' (определение восстанавливается из снимка, сделанного при входе через BEDIT). Активным становится пространство, в котором пользователь был до BEDIT.",
        examples: ["BCLOSE", "БЗАКРЫТЬ"],
        seeAlso: ["BEDIT", "BSAVE"],
      },
      en: {
        short: "Close the active Block Editor session.",
        long: "The BCLOSE command ends the current Block Editor session. The user picks action='save' (changes remain in the block definition) or action='discard' (the definition is restored from the snapshot taken on BEDIT enter). The previously-active space is reactivated.",
        examples: ["BCLOSE"],
        seeAlso: ["BEDIT", "BSAVE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.bclose.label",
      labelKeyEn: "command.bclose.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "space/exit-block-edit", file: BLOCK_EDIT_REDUCER },
    ],
  },

  {
    id: "BSAVE",
    category: "block",
    status: "implemented",
    // Block-editor session lifecycle (persist edits without leaving) — not an
    // AI-callable command (cf. ТЗ §10.1 BEDIT = no).
    aiCallable: false,
    // D5 — "BS" is App.tsx's in-app short form, present in
    // shellKeyboardCommandAliases but missing here.
    aliases: { en: ["BSAVE", "BS"], ru: ["БСОХРАНИТЬ"] },
    commandTypeLiterals: ["space/save-block-edit"],
    draftCommandId: "BSAVE",
    parameters: [],
    help: {
      ru: {
        short: "Сохранить состояние блока без выхода из редактора.",
        long: "Команда BSAVE фиксирует текущее состояние определения блока как новый базовый снимок. После BSAVE последующая команда BCLOSE с action='discard' откатит изменения, сделанные ПОСЛЕ сохранения, но не отменит то, что было сохранено. Активное пространство Block Editor не меняется.",
        examples: ["BSAVE", "БСОХРАНИТЬ"],
        seeAlso: ["BEDIT", "BCLOSE"],
      },
      en: {
        short: "Save the current block state without leaving the editor.",
        long: "The BSAVE command commits the current block definition state as a new baseline snapshot. After BSAVE, a subsequent BCLOSE with action='discard' will roll back changes made AFTER the save without undoing what was already saved. The Block Editor space stays active.",
        examples: ["BSAVE"],
        seeAlso: ["BEDIT", "BCLOSE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.bsave.label",
      labelKeyEn: "command.bsave.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "space/save-block-edit", file: BLOCK_EDIT_REDUCER },
    ],
  },

  {
    // Coordination: general EXPLODE (PLINE/HATCH/DIM/etc) lives in
    // modify.ts; this descriptor only owns the INSERT-specific literal.
    id: "EXPLODEINSERT",
    category: "block",
    status: "implemented",
    // aiCallable:false — EXPLODEINSERT is a distinct registry id, not in ТЗ §10.1
    // (which lists EXPLODE) + no shipped ai-commands action targets it (the EXPLODE
    // action targets command id EXPLODE, which owns the geometry/explode-insert
    // literal); AI surface deferred to CC-2c + explicit ТЗ §17 revision.
    aiCallable: false,
    aliases: { en: ["XPLODE"], ru: ["РАСЧЛЕНИТЬБЛОК"] },
    commandTypeLiterals: ["geometry/explode-insert"],
    parameters: [
      {
        name: "insertId",
        type: "entityId",
        required: true,
        description: {
          ru: "Идентификатор INSERT, который нужно расчленить.",
          en: "Identifier of the INSERT entity to explode.",
        },
      },
      {
        name: "resolveNested",
        type: "enum",
        enumValues: ["none", "one-level"],
        required: false,
        defaultValue: "one-level",
        description: {
          ru: "none — оставить вложенные блоки как INSERTы, one-level — расчленить и их (XPLODE).",
          en: "none — leave nested blocks as INSERTs, one-level — also explode them (XPLODE).",
        },
      },
    ],
    help: {
      ru: {
        short: "Расчленить экземпляр блока на дочерние сущности.",
        long: "Команда расчленяет указанный INSERT, заменяя его дочерними сущностями определения блока. Учитывает наследование BYBLOCK, композицию матриц трансформации, превращение Circle/Arc в Ellipse при non-uniform scale и продвижение ATTRIB → Text. Параметр resolveNested='one-level' дополнительно расчленяет вложенные INSERTы (поведение XPLODE). Для общего EXPLODE по любым типам сущностей используйте команду EXPLODE.",
        examples: ["XPLODE 0F2A", "EXPLODEINSERT 1B33"],
        seeAlso: ["EXPLODE", "INSERT", "BLOCK"],
      },
      en: {
        short: "Explode a block INSERT into its child entities.",
        long: "The command explodes the given INSERT, replacing it with the block definition's child entities. It accounts for BYBLOCK inheritance, transform matrix composition, Circle/Arc → Ellipse promotion under non-uniform scale, and ATTRIB → Text promotion. resolveNested='one-level' additionally explodes nested INSERTs (XPLODE behavior). For the general-purpose EXPLODE across all explodable entity kinds, use the EXPLODE command.",
        examples: ["XPLODE 0F2A", "EXPLODEINSERT 1B33"],
        seeAlso: ["EXPLODE", "INSERT", "BLOCK"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.explode.label",
      labelKeyEn: "command.explode.label",
      ribbonTab: "modify",
      ribbonSection: "modify",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "geometry/explode-insert", file: GEOMETRY_REDUCER },
    ],
  },
];
